import { useEffect, useMemo, useState } from 'react';
import {
    IconChevronLeft, IconSettings, IconPuzzle, IconTrash, IconKey, IconRefresh,
    IconBrandChrome, IconBrandFirefox,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import customerService from '../../../services/sohozJobCustomerService';
import {
    EXTENSION_DOWNLOAD_URL,
    EXTENSION_DOWNLOAD_URL_FIREFOX,
    detectExtensionBrowser,
} from '../../../constants/extension';

export default function SohozJobSettings() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Show the store that matches the visitor's browser: Firefox gets AMO, everyone else the Chrome Web Store.
    const isFirefox = useMemo(() => detectExtensionBrowser() === 'firefox', []);
    const ext = isFirefox
        ? {
            url: EXTENSION_DOWNLOAD_URL_FIREFOX,
            badge: '/images/firefox-addons/get-the-addon-fx-apr-2020.svg',
            Icon: IconBrandFirefox,
            tp: 'sohozJob.ext.setup.firefox',
        }
        : {
            url: EXTENSION_DOWNLOAD_URL,
            badge: '/images/chrome-web-store/badge-bordered.png',
            Icon: IconBrandChrome,
            tp: 'sohozJob.ext.setup',
        };

    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);

    const loadKeys = async ({ silent = false } = {}) => {
        silent ? setRefreshing(true) : setLoading(true);
        try {
            setKeys(await customerService.listKeys());
        } catch {
            notifications.show({ title: t('common.error'), message: t('sohozJob.ext.loadFailed'), color: 'red' });
        } finally {
            silent ? setRefreshing(false) : setLoading(false);
        }
    };
    useEffect(() => { loadKeys(); }, []);

    const handleRevoke = async () => {
        try {
            await customerService.revokeKey(revokeTarget.id);
            notifications.show({ title: t('common.success'), message: t('sohozJob.ext.revoked'), color: 'green' });
            setRevokeTarget(null);
            loadKeys({ silent: true });
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('sohozJob.ext.revokeFailed'), color: 'red' });
        }
    };

    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : t('sohozJob.ext.never'));

    return (
        <div className="w-full font-hind space-y-5 sm:space-y-6 py-2 sm:py-4">

            {/* ── Header ── */}
            <div>
                <button
                    onClick={() => navigate('/tools/form-auto-fillup')}
                    className="inline-flex items-center gap-1 text-[13px] font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors mb-3"
                >
                    <IconChevronLeft size={15} /> {t('sohozJob.backToList')}
                </button>
                <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 flex items-center justify-center flex-shrink-0">
                        <IconSettings size={22} stroke={1.7} />
                    </span>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                            {t('sohozJob.settingsTitle')}
                        </h1>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">{t('sohozJob.settingsDesc')}</p>
                    </div>
                </div>
            </div>

            {/* ── Browser extension (connected devices) ── */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                    <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                            <IconPuzzle size={17} stroke={1.7} />
                        </span>
                        <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{t('sohozJob.ext.title')}</h2>
                    </div>
                    <button
                        onClick={() => loadKeys({ silent: true })}
                        disabled={refreshing}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-60"
                    >
                        <IconRefresh size={15} className={refreshing ? 'animate-spin' : ''} /> {t('sohozJob.ext.refresh')}
                    </button>
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{t('sohozJob.ext.desc')}</p>

                {/* Connected devices (keys) */}
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="w-6 h-6 border-2 border-zinc-300 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : keys.length === 0 ? (
                    <p className="text-sm text-zinc-400 dark:text-zinc-500 py-2">{t('sohozJob.ext.noKeys')}</p>
                ) : (
                    <div className="border border-zinc-100 dark:border-zinc-800 rounded-xl divide-y divide-zinc-100 dark:divide-zinc-800">
                        {keys.map((k) => {
                            const expired = !!k.revokedAt;
                            return (
                                <div key={k.id} className="flex items-center gap-3 px-4 py-3">
                                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${expired ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'}`}>
                                        <IconKey size={15} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-mono truncate flex items-center gap-2 flex-wrap">
                                            <span className={expired ? 'text-zinc-400 dark:text-zinc-500' : 'text-zinc-800 dark:text-zinc-100'}>{k.tokenPrefix}…</span>
                                            {expired && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold font-hind bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400">
                                                    {t('sohozJob.ext.expired')}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                                            {t('sohozJob.ext.lastUsed')}: {fmtDate(k.lastUsedAt)} · {t('sohozJob.ext.created')}: {fmtDate(k.createdAt)}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setRevokeTarget(k)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                                    >
                                        <IconTrash size={14} /> {t('sohozJob.ext.revoke')}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── Setup / install instructions ── */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                            <ext.Icon size={17} stroke={1.7} />
                        </span>
                        <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{t('sohozJob.ext.setup.title')}</h2>
                    </div>
                    {/* Official store badge — Chrome Web Store or Firefox Add-ons (AMO), matched to the visitor's browser */}
                    <a
                        href={ext.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex flex-shrink-0 no-underline opacity-100 transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        aria-label={t(`${ext.tp}.download`)}
                    >
                        <img
                            src={ext.badge}
                            alt={t(`${ext.tp}.download`)}
                            className="h-[44px] w-auto sm:h-[52px]"
                            decoding="async"
                        />
                    </a>
                </div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{t(`${ext.tp}.intro`)}</p>

                <ol className="relative">
                    {[1, 2, 3].map((n, i, arr) => (
                        <li key={n} className="relative flex gap-3.5 pb-6 last:pb-0">
                            {/* vertical connector */}
                            {i < arr.length - 1 && (
                                <span aria-hidden="true" className="absolute left-[13px] top-7 bottom-0 w-px bg-zinc-200 dark:bg-zinc-700" />
                            )}
                            <span className="relative z-[1] w-[27px] h-[27px] rounded-full bg-primary text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0 shadow-sm">
                                {n}
                            </span>
                            <span className="pt-1 text-sm text-zinc-600 dark:text-zinc-300">
                                {t(`${ext.tp}.step${n}`)}
                            </span>
                        </li>
                    ))}
                </ol>
            </div>

            <ConfirmDialog
                open={!!revokeTarget}
                onClose={() => setRevokeTarget(null)}
                onConfirm={handleRevoke}
                title={t('sohozJob.ext.revokeTitle')}
                description={t('sohozJob.ext.revokeConfirm')}
            />
        </div>
    );
}
